import { useState, useEffect } from 'react';
import axios from 'axios';
import CompCreateProv from './createProv';
import { FaPlus, FaSearch, FaDollarSign, FaBox, FaTruck } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';  
import { Link } from 'react-router-dom';

// ✅ MOVER ESTOS COMPONENTES FUERA DEL COMPONENTE PRINCIPAL
const SectionCard = ({ title, icon, children }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            {icon && <span className="mr-2 text-blue-500">{icon}</span>}
            {title}
        </h3>
        {children}
    </div>
);

const InputField = ({ label, icon, ...props }) => (
    <div className="mb-4">
        <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            {icon && <span className="mr-2 text-blue-500">{icon}</span>}
            {label}
        </label>
        <input
            {...props}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        />
    </div>
);

const IngresoStock = () => {
    const [productos, setProductos] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const { token } = useAuth();
    const caja = localStorage.getItem('cajaAbiertaId');

    const [searchTerm, setSearchTerm] = useState('');
    const [form, setForm] = useState({
        producto_id: '',
        cantidad_unidades: '',
        proveedor_id: '',
        precio: '',
        precio_compra: '',
        precio_mayorista_a: '',
        precio_mayorista_b: '',
        precio_mayorista_c: '',
        forma_pago: 'efectivo',
    });
    const [filteredProductos, setFilteredProductos] = useState([]);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);

    // Cargar datos de productos y proveedores
    const fetchData = async () => {
        try {
            const [responseProductos, responseProveedores] = await Promise.all([
                axios.get(`${import.meta.env.VITE_API_URL}/productos/admin`, {
                    headers: {
                        'x-auth-token': token,
                    },
                }),
                axios.get(`${import.meta.env.VITE_API_URL}/proveedores`, {
                    headers: {
                        'x-auth-token': token,
                    },
                }),
            ]);

            setProductos(responseProductos.data.productos);
            setProveedores(responseProveedores.data.proveedores);
        } catch (error) {
            console.error('Error al cargar datos:', error);
            setError('No se pudo cargar la lista de productos o proveedores.');
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const results = productos.filter((producto) => {
            const matchByBarcode = producto.codigo_barras && Array.isArray(producto.codigo_barras) && producto.codigo_barras.some((codigo) => {
                if (codigo) {
                    const codigoString = String(codigo.codigo).toLowerCase();
                    return codigoString.includes(searchTerm.toLowerCase());
                }
                return false;
            });
            const matchByName = !matchByBarcode && producto.nombre_producto.toLowerCase().includes(searchTerm.toLowerCase());
            return matchByBarcode || matchByName;
        });
        setFilteredProductos(results);
    }, [searchTerm, productos]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({
            ...form,
            [name]: value,
        });
    };

    const resetForm = () => {
        setForm({
            producto_id: '',
            cantidad_unidades: '',
            proveedor_id: '',
            precio: '',
            precio_compra: '',
            precio_mayorista_a: '',
            precio_mayorista_b: '',
            precio_mayorista_c: '',
            forma_pago: 'efectivo',
        });
        setSearchTerm('');
    };

    const handleSubmit = async (e) => {
        if (!caja) {
            alert('No puedes realizar ingresos sin abrir una caja');
            return
        }
        e.preventDefault();
        setError('');

        try {
            await axios.post(`${import.meta.env.VITE_API_URL}/ingresos/`, form, {
                headers: {
                    'x-auth-token': token,
                },
            });

            alert('Stock ingresado con éxito');
            resetForm();
            fetchData();
        } catch (error) {
            console.error('Error al registrar el ingreso de stock:', error);
            setError('Error al registrar el ingreso de stock. Verifica los datos ingresados.');
        }
    };

    const handleSelectProducto = (productoId, productoName) => {
        if (!caja) {
            alert('No puedes realizar ingresos sin abrir una caja');
            return
        }
        const productoSeleccionado = productos.find((producto) => producto.id === productoId);

        setForm({
            ...form,
            producto_id: productoId,
            proveedor_id: '',
            precio: productoSeleccionado.precios[0]?.precio || '',
            precio_compra: productoSeleccionado.precios[0]?.precio_compra || '',
            precio_mayorista_a: productoSeleccionado.precios[0]?.precio_mayorista_a || '',
            precio_mayorista_b: productoSeleccionado.precios[0]?.precio_mayorista_b || '',
            precio_mayorista_c: productoSeleccionado.precios[0]?.precio_mayorista_c || '',
        });

        // Limpiar el término de búsqueda para ocultar las sugerencias
        setSearchTerm('');
        setFilteredProductos([]);
    };

    // Agregar función para limpiar selección
    const handleClearSelection = () => {
        setForm({
            ...form,
            producto_id: '',
            proveedor_id: '',
            precio: '',
            precio_compra: '',
            precio_mayorista_a: '',
            precio_mayorista_b: '',
            precio_mayorista_c: '',
        });
        setSearchTerm('');
        setFilteredProductos([]);
    };

    const handleOpenCreateProv = () => setOpen(true);
    const onClose = () => setOpen(false);

    const onCreateProveedor = async () => {
        try {
            const responseProveedores = await axios.get(`${import.meta.env.VITE_API_URL}/proveedores`, {
                headers: {
                    'x-auth-token': token,
                },
            });
            setProveedores(responseProveedores.data.proveedores);
        } catch (error) {
            console.error('Error al actualizar lista de proveedores:', error);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2">Ingreso de Stock</h1>
                    <p className="text-gray-600">Registra nuevas mercaderías en el inventario</p>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                <motion.form 
                    onSubmit={handleSubmit} 
                    className="space-y-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Búsqueda de Producto */}
                    <SectionCard title="Seleccionar Producto" icon={<FaSearch />}>
                        <div className="relative">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Buscar por nombre o plug"
                                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                {form.producto_id && (
                                    <button
                                        type="button"
                                        onClick={handleClearSelection}
                                        className="px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                                        title="Cambiar producto"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>

                            {/* Mostrar producto seleccionado */}
                            {form.producto_id && !searchTerm && (
                                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <p className="text-sm text-green-800">
                                        <span className="font-medium">✓ Producto seleccionado:</span> {
                                            productos.find(p => p.id === parseInt(form.producto_id))?.nombre_producto || 'Producto seleccionado'
                                        }
                                    </p>
                                    <p className="text-xs text-green-600 mt-1">
                                        Para cambiar el producto, presiona el botón ✕ o escribe en el campo de búsqueda
                                    </p>
                                </div>
                            )}

                            {/* Mostrar lista de sugerencias solo cuando hay texto de búsqueda */}
                            {searchTerm && (
                                <>
                                    {filteredProductos.length > 0 ? (
                                        <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                            {filteredProductos.map((producto) => (
                                                <li
                                                    key={producto.id}
                                                    onClick={() => handleSelectProducto(producto.id, producto.nombre_producto)}
                                                    className={`p-4 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                                                        form.producto_id === producto.id ? 'bg-green-50 border-green-200' : ''
                                                    }`}
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <div>
                                                            <p className="font-medium text-gray-900">{producto.nombre_producto}</p>
                                                            {producto.codigo_barras && producto.codigo_barras.length > 0 && (
                                                                <p className="text-sm text-gray-500">
                                                                    Códigos: {producto.codigo_barras.map(cb => cb.codigo).join(", ")}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="text-right">
                                                            <span className={`text-sm font-medium ${producto.stock_unidades <= 0 ? "text-red-600" : "text-green-600"}`}>
                                                                Stock: {producto.stock_unidades}
                                                            </span>
                                                            {form.producto_id === producto.id && (
                                                                <p className="text-xs text-green-600 font-medium">✓ Seleccionado</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
                                            <p className="text-center text-gray-500 mb-3">
                                                No se encontraron productos que coincidan con "<span className="font-medium">{searchTerm}</span>"
                                            </p>
                                            <div className="flex flex-col gap-2">
                                                <Link
                                                    to={`/crear-producto?plug=${encodeURIComponent(searchTerm)}`}
                                                    className="block text-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                                >
                                                    🏷️ Crear producto con código: <span className="font-medium">{searchTerm}</span>
                                                </Link>
                                                <Link
                                                    to={`/crear-producto?name=${encodeURIComponent(searchTerm)}`}
                                                    className="block text-center px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                                                >
                                                    📦 Crear producto con nombre: <span className="font-medium">{searchTerm}</span>
                                                </Link>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </SectionCard>

                    {/* Cantidad y Proveedor */}
                    <SectionCard title="Detalles del Ingreso" icon={<FaBox />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputField
                                label="Cantidad de Unidades"
                                icon={<FaBox />}
                                type="number"
                                name="cantidad_unidades"
                                value={form.cantidad_unidades}
                                onChange={handleChange}
                                step="0.001"
                                min="0.001"
                                placeholder="Ej: 10.525"
                                required
                            />

                            <div className="mb-4">
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                    <FaTruck className="mr-2 text-blue-500" />
                                    Proveedor
                                </label>
                                <div className="flex gap-2">
                                    <select
                                        name="proveedor_id"
                                        value={form.proveedor_id}
                                        onChange={handleChange}
                                        required
                                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="" disabled>Seleccione un proveedor</option>
                                        {proveedores.map((proveedor) => (
                                            <option key={proveedor.id} value={proveedor.id}>
                                                {proveedor.nombre}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={handleOpenCreateProv}
                                        className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                                    >
                                        <FaPlus />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </SectionCard>

                    {/* Precios */}
                    <SectionCard title="Gestión de Precios" icon={<FaDollarSign />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <InputField
                                label="Precio de Venta"
                                icon={<FaDollarSign />}
                                type="number"
                                name="precio"
                                value={form.precio}
                                onChange={handleChange}
                                step="0.01"
                                min="0.01"
                                placeholder="0.00"
                                required
                            />

                            <InputField
                                label="Precio sin IVA"
                                icon={<FaDollarSign />}
                                type="number"
                                name="precio_compra"
                                value={form.precio_compra}
                                onChange={handleChange}
                                step="0.01"
                                min="0.01"
                                placeholder="0.00"
                                required
                            />

                            <InputField
                                label="Precio Mayorista A"
                                icon={<FaDollarSign />}
                                type="number"
                                name="precio_mayorista_a"
                                value={form.precio_mayorista_a}
                                onChange={handleChange}
                                step="0.01"
                                placeholder="0.00"
                            />

                            <InputField
                                label="Precio Mayorista B"
                                icon={<FaDollarSign />}
                                type="number"
                                name="precio_mayorista_b"
                                value={form.precio_mayorista_b}
                                onChange={handleChange}
                                step="0.01"
                                placeholder="0.00"
                            />

                            <InputField
                                label="Precio Mayorista C"
                                icon={<FaDollarSign />}
                                type="number"
                                name="precio_mayorista_c"
                                value={form.precio_mayorista_c}
                                onChange={handleChange}
                                step="0.01"
                                placeholder="0.00"
                            />

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Forma de Pago
                                </label>
                                <select
                                    name="forma_pago"
                                    value={form.forma_pago}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="efectivo">Efectivo</option>
                                    <option value="tarjeta">Tarjeta</option>
                                    <option value="transferencia">Transferencia</option>
                                    <option value="otros">Otros</option>
                                    <option value="cuentaCorriente">Cuenta Corriente</option>
                                </select>
                            </div>
                        </div>
                    </SectionCard>

                    {/* Botón Submit */}
                    <div className="flex justify-center pt-6">
                        <button
                            type="submit"
                            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 transition-all duration-200"
                        >
                            ✨ Registrar Ingreso
                        </button>
                    </div>
                </motion.form>

                {/* Modal para crear proveedor */}
                {open && (
                    <CompCreateProv 
                        open={open} 
                        onClose={onClose} 
                        onCreateProveedor={onCreateProveedor} 
                    />
                )}
            </div>
        </div>
    );
};

export default IngresoStock;
